import { X } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/**
 * Native <dialog> as a modal: focus trap and Esc come from the browser. A visible "Zamknij"
 * button and a click beside the box close it too: not everyone knows Esc (owner, 2026-10-10).
 */
export function Dialog({
  open,
  onClose,
  title,
  wide = false,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** For a photo: most of the screen instead of a form-sized box. */
  wide?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  const pressedBackdrop = useRef(false);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard closes the native dialog with Esc; this click is only the backdrop.
    <dialog
      ref={ref}
      onClose={onClose}
      // A click on the backdrop lands on the <dialog> itself, never on its content. Both ends of
      // the click must: a drag from a field (selecting text) released beside the box fires a click
      // on the <dialog> too, and closing then would lose a drawn sector or a one-time QR code.
      onPointerDown={(e) => {
        pressedBackdrop.current = e.target === ref.current;
      }}
      onClick={(e) => {
        if (pressedBackdrop.current && e.target === ref.current) ref.current?.close();
        pressedBackdrop.current = false;
      }}
      className={`m-auto w-full ${wide ? "max-w-5xl" : "max-w-md"} rounded-lg border border-zinc-700 bg-zinc-900 p-0 text-zinc-100 backdrop:bg-black/60`}
    >
      {open && (
        <div className="flex flex-col gap-4 p-5">
          <div className="flex items-start gap-3">
            <h2 className="flex-1 text-base font-semibold">{title}</h2>
            <button
              type="button"
              onClick={() => ref.current?.close()}
              className="flex shrink-0 items-center gap-1 rounded-md bg-zinc-800 px-2.5 py-1.5 text-sm font-medium text-zinc-100 hover:bg-zinc-700"
            >
              <X size={16} /> {t("common.close")}
            </button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}
